import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Crown, Medal } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api, RANKS } from "../lib/api";

export const Leaderboard = ({ onBack }) => {
  const { t, lang, dict } = useLang();
  const [domains, setDomains] = useState([]);
  const [filter, setFilter] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDomains(lang).then(setDomains).catch(() => {});
  }, [lang]);

  useEffect(() => {
    setLoading(true);
    api
      .getLeaderboard(filter)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [filter]);

  const medalColor = (i) => (i === 0 ? "#fbbf24" : i === 1 ? "#d1d5db" : "#cd7f32");

  return (
    <div className="flex min-h-full flex-col px-5 py-6" data-testid="leaderboard-screen">
      <button
        onClick={onBack}
        data-testid="leaderboard-back"
        className="mb-4 flex w-fit items-center gap-2 text-sm font-medium text-white/50 transition-colors hover:text-white"
      >
        <ArrowLeft size={18} /> {t("back")}
      </button>

      <h2 className="flex items-center gap-2 font-display text-3xl font-extrabold uppercase tracking-tight">
        <Crown className="text-[#fbbf24]" /> {t("leaderboard")}
      </h2>

      {/* Filtres */}
      <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
        <FilterChip active={filter === null} label={t("global")} onClick={() => setFilter(null)} testid="filter-global" />
        {domains.map((d) => (
          <FilterChip
            key={d.id}
            active={filter === d.id}
            label={d.nom}
            color={d.color}
            onClick={() => setFilter(d.id)}
            testid={`filter-${d.id}`}
          />
        ))}
      </div>

      <div className="mt-4 flex-1">
        {loading ? (
          <p className="animate-pulse-glow py-10 text-center text-white/50">{t("loading")}</p>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-white/45">{t("noScores")}</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r, i) => {
              const rc = RANKS[r.rank] || RANKS.anonyme;
              return (
                <motion.div
                  key={r.id || i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.5) }}
                  data-testid={`leaderboard-row-${i}`}
                  className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3"
                >
                  <div className="flex w-8 justify-center">
                    {i < 3 ? (
                      <Medal size={20} style={{ color: medalColor(i) }} />
                    ) : (
                      <span className="font-display text-sm font-bold text-white/40">{i + 1}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-bold text-white">{r.pseudo || "Anonyme"}</p>
                    <p className="text-xs uppercase tracking-wide" style={{ color: rc.c1 }}>
                      {dict.ranks[r.rank] || ""}
                    </p>
                  </div>
                  <span className="font-display text-lg font-black text-white">
                    {(r.score || 0).toLocaleString()}
                  </span>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const FilterChip = ({ active, label, onClick, color = "#f093fb", testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
      active ? "text-white" : "border-white/15 bg-white/5 text-white/60 hover:bg-white/10"
    }`}
    style={active ? { background: color, borderColor: color } : {}}
  >
    {label}
  </button>
);
